import React, { useState, ChangeEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { JsonParam, useQueryParam, withDefault } from 'use-query-params'
import Cookies from 'js-cookie'
import { useDispatch } from 'react-redux'
import { SearchIcon } from '../../../shared-components/Icons'
import { useCustomNavigation } from '../../../../hooks/useCustomNavigate'
import { setSearch } from '../../../../redux/reducers/searchReducer'
import {
  constants,
  textAndImage,
  IFilters,
  initialFiltersConst,
  NavigationEnum,
} from '../../../../utils/constants'
import styles from './HeroSection.module.scss'

export const HeroSection = () => {
  const { t } = useTranslation()
  const customNavigate = useCustomNavigation()
  const dispatch = useDispatch()
  
  const [currentSlide, setCurrentSlide] = useState(0)
  const [searchValue, setSearchValue] = useState('')
  const [cookieTags, setCookieTags] = useState<string[]>([])
  
  const initialFilters = withDefault(JsonParam, initialFiltersConst)
  const [filters, setFilters] = useQueryParam<IFilters>('filters', initialFilters)

  // Default popular search tags (fallback when no cookies)
  const defaultPopularTags = ['JPG', 'EPS', 'PNG']

  // Load cookie tags on component mount
  useEffect(() => {
    const currentSearchTermsCookies = Cookies.get('searchTermsCookie')
    const parsedSearchTermsCookies = currentSearchTermsCookies
      ? JSON.parse(currentSearchTermsCookies)
      : []
    setCookieTags(parsedSearchTermsCookies)
  }, [])

  // Function to update cookie with max 10 tags (FIFO)
  const updateSearchCookie = (newTag: string) => {
    const currentSearchTermsCookies = Cookies.get('searchTermsCookie')
    const parsedSearchTermsCookies = currentSearchTermsCookies
      ? JSON.parse(currentSearchTermsCookies)
      : []
    
    // Remove tag if it already exists to avoid duplicates
    const filteredTags = parsedSearchTermsCookies.filter((tag: string) => tag !== newTag)
    
    // Add new tag to the end
    const updatedTags = [...filteredTags, newTag]
    
    // Keep only last 10 tags (FIFO - remove from beginning if more than 10)
    const finalTags = updatedTags.slice(-10)
    
    Cookies.set('searchTermsCookie', JSON.stringify(finalTags))
    setCookieTags(finalTags)
    
    return finalTags
  }

  // Function to remove tag from cookie
  const removeTagFromCookie = (tagToRemove: string) => {
    const currentSearchTermsCookies = Cookies.get('searchTermsCookie')
    const parsedSearchTermsCookies = currentSearchTermsCookies
      ? JSON.parse(currentSearchTermsCookies)
      : []
    
    const updatedTags = parsedSearchTermsCookies.filter((tag: string) => tag !== tagToRemove)
    
    Cookies.set('searchTermsCookie', JSON.stringify(updatedTags))
    setCookieTags(updatedTags)
  }

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  const handleSearchPressed = () => {
    if (searchValue?.trim()) {
      updateSearchCookie(searchValue.trim())
    }
    
    customNavigate(
      `${NavigationEnum.ASSETS}?filters=${encodeURIComponent(
        JSON.stringify({
          ...filters,
          searchFilter: searchValue,
        })
      )}`
    )
    dispatch(setSearch(searchValue))
  }

  const handleTagClick = (tag: string) => {
    setSearchValue(tag)
    updateSearchCookie(tag)
    
    customNavigate(
      `${NavigationEnum.ASSETS}?filters=${encodeURIComponent(
        JSON.stringify({
          ...filters,
          searchFilter: tag,
        })
      )}`
    )
    dispatch(setSearch(tag))
  }

  const handleTagRemove = (event: React.MouseEvent, tagToRemove: string) => {
    event.stopPropagation() // Prevent tag click when removing
    removeTagFromCookie(tagToRemove)
  }

  const handleSlideChange = (newSlide: number) => {
    setCurrentSlide(newSlide)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearchPressed()
    }
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    setSearchValue(filters?.searchFilter || '')
  }, [filters?.searchFilter])

  // Get tags to display (cookie tags in reverse order, or default tags)
  const tagsToDisplay = cookieTags.length > 0 ? [...cookieTags].reverse() : defaultPopularTags
  const isShowingCookieTags = cookieTags.length > 0

  return (
    <div className={styles.heroContainer}>
      {/* Background Images */}
      <div className={styles.imageContainer}>
        {[constants.img_background1, constants.img_background2, constants.img_background3].map((img, index) => (
          <div
            key={index}
            className={`${styles.imageSlide} ${currentSlide === index ? styles.active : ''}`}
          >
            <img 
              src={img} 
              alt="" 
              className={styles.image}
            />
            <div className={styles.overlay}></div>
          </div>
        ))}
      </div>

      {/* Content Overlay */}
      <div className={styles.contentOverlay}>
        <div className={`${styles.titleContainer} animate`}>
          <h2 className={styles.title}>
            {textAndImage[currentSlide].title}
          </h2>
        </div>
        
        <div className={`${styles.descriptionContainer} animate`}>
          <p className={styles.description}>
            {textAndImage[currentSlide].description}
          </p>
        </div>
        
        {/* Search Bar */}
        <div className={`${styles.searchContainer} animate`}>
          <div className={styles.searchWrapper}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type="text"
              value={searchValue}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              placeholder={t('Search_Placeholder') || "Search millions of high-quality images..."}
              className={styles.searchInput}
              autoComplete="off"
            />
            <button 
              className={`${styles.searchButton} button`}
              onClick={handleSearchPressed}
            >
              {t('Search') || 'Search'}
            </button>
          </div>
        </div>

        {/* Popular/Recent searches */}
        <div className={`${styles.tagsContainer} animate`}>
          <span className={styles.tagLabel}>
            {isShowingCookieTags ? (t('Recent') || 'Recent') : (t('Popular') || 'Popular')}:
          </span>
          {tagsToDisplay.map((tag, index) => (
            <div 
              key={`${tag}-${index}`} 
              className={styles.tagWrapper}
            >
              <span 
                className={styles.tag}
                onClick={() => handleTagClick(tag)}
              >
                {tag}
              </span>
              {isShowingCookieTags && (
                <button
                  className={styles.tagRemove}
                  onClick={(event) => handleTagRemove(event, tag)}
                  aria-label={`Remove ${tag} tag`}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Slide Indicators */}
      <div className={styles.indicators}>
        {[0, 1, 2].map((index) => (
          <button
            key={index}
            onClick={() => handleSlideChange(index)}
            className={`${styles.indicator} ${currentSlide === index ? styles.indicatorActive : ''}`}
          />
        ))}
      </div>
    </div>
  )
}